<script setup lang="ts">
  import { computed } from 'vue';
  import { Switch } from '@/shared/ui';
  import { useMatrixFillLabContext } from '../../model/useMatrixFillLab';
  import JsonBlock from './JsonBlock.vue';
  import LabSection from './LabSection.vue';
  import SimulatePanel from './SimulatePanel.vue';

  const lab = useMatrixFillLabContext();

  const counters = computed(() => {
    const summary = lab.inputSummary.value;
    return [
      { label: 'elementos', value: summary.elements },
      { label: 'campos', value: summary.fields },
      { label: 'tabelas', value: summary.tables },
      { label: 'regras', value: summary.rules },
      { label: 'permissões', value: summary.grants },
      { label: 'âncoras', value: summary.anchors },
      { label: 'campos de relatório', value: summary.reportFields },
    ];
  });

  const storedRuleCount = computed(() => lab.storedRules.value?.rules.length ?? 0);
  const storedGrantCount = computed(() => lab.storedPermissions.value?.fields.length ?? 0);
</script>

<template>
  <LabSection
    title="Entrada: o que a matriz define"
    hint="Tudo que o compilador recebe vem daqui: os três JSONs salvos pelo construtor, mais o que você simula abaixo. Nenhum código por matriz."
    data-testid="lab-input"
  >
    <div class="flex flex-wrap gap-1.5">
      <span
        v-for="counter in counters"
        :key="counter.label"
        class="rounded-full bg-[var(--color-gray-09)] px-2 py-0.5 text-[11px]"
      >
        <span class="font-semibold">{{ counter.value }}</span>
        {{ counter.label }}
      </span>
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="flex cursor-pointer items-center gap-2">
        <Switch
          v-model="lab.useStoredRules.value"
          data-testid="toggle-stored-rules"
        />
        <span>Usar as {{ storedRuleCount }} regra(s) condicionais salvas na matriz</span>
      </label>
      <label class="flex cursor-pointer items-center gap-2">
        <Switch
          v-model="lab.useStoredPermissions.value"
          data-testid="toggle-stored-permissions"
        />
        <span>Usar as {{ storedGrantCount }} permissão(ões) salvas na matriz</span>
      </label>
      <p class="text-[10px] text-[var(--color-gray-05)]">
        Âncoras e campos de relatório continuam valendo com as chaves desligadas: são configuração da matriz, não do cenário.
      </p>
    </div>

    <div class="flex flex-col gap-1.5">
      <JsonBlock
        title="Regras em uso"
        :value="lab.rules.value"
      />
      <JsonBlock
        title="Permissões em uso"
        :value="lab.permissions.value"
      />
      <JsonBlock
        title="Amostra: primeiro elemento de cada tipo"
        :value="lab.elementSamples.value"
      />
    </div>

    <SimulatePanel />
  </LabSection>
</template>
